import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../utils/urls";
import api from "../utils/api";

interface Props {
  setShowForm: (value: boolean) => void;
  categories: [];
}

const AddInventoryItem: React.FC<Props> = ({ setShowForm, categories }) => {
  console.log(categories, "CATEGORIES");
  const [inventoryList, setInventoryList] = useState([
    {
      itemName: "",
      description: "",
      categories: "",
      image: null,
      imagePreview: "",
      variants: [{ variantName: "", price: "", stock: "", sku: "" }],
    },
  ]);

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.key === "Escape") setShowForm(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const addInventoryItem = () => {
    setInventoryList([
      ...inventoryList,
      {
        itemName: "",
        description: "",
        categories: "",
        image: null,
        imagePreview: "",
        variants: [{ variantName: "", price: "", stock: "", sku: "" }],
      },
    ]);
  };

  const removeInventoryItem = (index: number) => {
    const updated = [...inventoryList];
    updated.splice(index, 1);
    setInventoryList(updated);
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      inventoryList.forEach((item, index) => {
        formData.append(`items[${index}][itemName]`, item.itemName);
        formData.append(`items[${index}][description]`, item.description);
        formData.append(`items[${index}][categories]`, item.categories);

        if (item.image) {
          formData.append(`items[${index}][image]`, item.image);
        }

        item.variants.forEach((variant, vIndex) => {
          formData.append(
            `items[${index}][variants][${vIndex}][variantName]`,
            variant.variantName
          );
          formData.append(
            `items[${index}][variants][${vIndex}][price]`,
            variant.price
          );
          formData.append(
            `items[${index}][variants][${vIndex}][stock]`,
            variant.stock
          );
          formData.append(
            `items[${index}][variants][${vIndex}][sku]`,
            variant.sku
          );
        });
      });

      await api.post(API_ENDPOINTS.INVENTORY.SAVE_DATA, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Items added successfully!");
      setShowForm(false);
      setInventoryList([
        {
          itemName: "",
          description: "",
          categories: "",
          image: null,
          imagePreview: "",
          variants: [{ variantName: "", price: "", stock: "", sku: "" }],
        },
      ]);
      setTimeout(() => {
        window.location.reload();
      }, 1000); // 1 second delay
    } catch (error) {
      toast.error("Failed to save items");
      console.error(error);
    }
  };

  const handleInventoryInputChange = (
    e: any,
    invIndex: number,
    field: string
  ) => {
    const updated: any = [...inventoryList];
    updated[invIndex][field] = e.target.value;
    setInventoryList(updated);
  };

  const handleVariantChange = (
    e: any,
    invIndex: number,
    varIndex: number,
    field: string
  ) => {
    const updated: any = [...inventoryList];
    updated[invIndex].variants[varIndex][field] = e.target.value;
    setInventoryList(updated);
  };

  const handleImageDrop = (e: any, invIndex: number) => {
    e.preventDefault();
    const file = e.dataTransfer?.files[0] || e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updated = [...inventoryList];
        updated[invIndex].image = file;
        updated[invIndex].imagePreview = reader.result as string;
        setInventoryList(updated);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="mt-6">
      {inventoryList.map((item, i) => (
        <div key={i} className="mb-8 border p-4 rounded shadow-sm bg-gray-50">
          <div className="mb-2">
            <input
              type="text"
              placeholder="Item Name"
              value={item.itemName}
              onChange={(e) => handleInventoryInputChange(e, i, "itemName")}
              className="w-full border p-2 mb-2"
            />
            <textarea
              placeholder="Description"
              value={item.description}
              onChange={(e) => handleInventoryInputChange(e, i, "description")}
              className="w-full border p-2 mb-3"
            />

            <select
              value={item.categories || ""}
              onChange={(e) => handleInventoryInputChange(e, i, "categories")}
              className="w-full border p-2 mb-3"
            >
              <option value="">Select Category</option>
              {categories.map((category: any) => (
                <option key={category.categoryId} value={category.categoryId}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Image Upload */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleImageDrop(e, i)}
              className="w-full h-32 border-dashed border-2 border-gray-400 flex items-center justify-center cursor-pointer mb-3 bg-white"
              onClick={() => document.getElementById(`fileInput-${i}`)?.click()}
            >
              {item.imagePreview ? (
                <img
                  src={item.imagePreview}
                  alt="Preview"
                  className="h-full object-contain"
                />
              ) : (
                <span className="text-gray-500">
                  Drag & Drop or Click to Upload
                </span>
              )}
              <input
                id={`fileInput-${i}`}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageDrop(e, i)}
                className="hidden"
              />
            </div>

            {/* Variants */}
            <h3 className="font-semibold mb-2">Variants</h3>
            {item.variants.map((variant, vi) => (
              <div key={vi} className="mb-2 border p-2 rounded">
                <input
                  type="text"
                  placeholder="Variant Name"
                  value={variant.variantName}
                  onChange={(e) => handleVariantChange(e, i, vi, "variantName")}
                  className="w-full border p-2 mb-2"
                />
                <input
                  type="number"
                  placeholder="Price"
                  value={variant.price}
                  onChange={(e) => handleVariantChange(e, i, vi, "price")}
                  className="w-full border p-2 mb-2"
                />
                <input
                  type="number"
                  placeholder="Stock"
                  value={variant.stock}
                  onChange={(e) => handleVariantChange(e, i, vi, "stock")}
                  className="w-full border p-2 mb-2"
                />
                <input
                  type="text"
                  placeholder="SKU"
                  value={variant.sku}
                  onChange={(e) => handleVariantChange(e, i, vi, "sku")}
                  className="w-full border p-2 mb-2"
                />
              </div>
            ))}
            <button
              className="bg-gray-200 px-3 py-1 rounded mb-3"
              onClick={() =>
                setInventoryList((prev) => {
                  const updated = [...prev];
                  updated[i].variants.push({
                    variantName: "",
                    price: "",
                    stock: "",
                    sku: "",
                  });
                  return updated;
                })
              }
            >
              + Add Variant
            </button>

            {inventoryList.length > 1 && (
              <button
                className="text-red-500 text-sm"
                onClick={() => removeInventoryItem(i)}
              >
                Remove This Item
              </button>
            )}
          </div>
        </div>
      ))}

      <button
        onClick={addInventoryItem}
        className="bg-blue-200 text-blue-800 px-4 py-2 rounded mb-4"
      >
        + Add Another Item
      </button>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded"
      >
        Save Inventory
      </button>
    </div>
  );
};

export default AddInventoryItem;
